import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { integrations as integrationsApi } from '@territory/client/api';
import { Splash } from './Splash';

/**
 * Landing route for the Google integrations OAuth (calendar scope) connect
 * flow. Google redirects the browser here:
 *   https://wedge.repvantage.io/integrations/callback?code=...&state=...
 *
 * Unlike the wedge SIGN-IN callback (/auth/callback, which the backend runs
 * server-side), the integrations OAuth uses the SPA-POST model: we must POST
 * { code, state, redirectUri } to /api/integrations/oauth/google/callback so
 * the backend exchanges the code and stores the encrypted LinkedAccount.
 *
 * Auth: the POST goes through the api client, which attaches the wedge Bearer
 * from localStorage (rv_wedge_*) — that survives the external-Safari OAuth
 * round trip even in an iOS-standalone PWA (cookies do not, which is the whole
 * reason the token bridge exists). The CSRF *state cookie* set by /start is
 * the one fragile piece on iOS-standalone; if it does not survive the handoff
 * the backend returns "OAuth state mismatch" and we land on ?calendar=error.
 *
 * On success we bounce to /?integration=connected — WedgeRoute already detects
 * that and pulls the live calendar into the Today feed.
 */
export function IntegrationsCallbackWedge() {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError || !code || !state) {
      navigate('/?calendar=error', { replace: true });
      return;
    }

    integrationsApi
      .oauthCallback({
        code,
        state,
        redirectUri: `${window.location.origin}/integrations/callback`,
      })
      .then(() => navigate('/?integration=connected', { replace: true }))
      .catch(() => {
        setFailed(true);
        setTimeout(() => navigate('/?calendar=error', { replace: true }), 1400);
      });
  }, [navigate]);

  return (
    <Splash label={failed ? 'Could not connect calendar' : 'Connecting your calendar'} />
  );
}
